import { ReactElement, useState } from 'react';
import { Link, NavLink, useLocation, useResolvedPath } from 'react-router-dom';
import { Icon } from '../../Icon';

import { NavItem } from './types';
import { useCheck } from 'core/permissions';
import { Box, MenuItem, MenuList, floatingContainerStyle, useRenderTransition } from '@ff-it/ui';
import {
  autoPlacement,
  autoUpdate,
  offset,
  size,
  safePolygon,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
  FloatingPortal,
  type AutoPlacementOptions,
} from '@floating-ui/react';
import { popMenuWrap, submenu, submenuItem } from './style.css';

export interface MenuProps {
  menu: NavItem[];
}

const sizeOptions: any = {
  apply({ availableHeight, elements }: any) {
    Object.assign(elements.floating.style, {
      maxHeight: `${availableHeight}px`,
    });
  },
  padding: 8,
};
const placementOptions: AutoPlacementOptions = { allowedPlacements: ['right-start', 'right', 'right-end'] };

// Use Floating UI instead of the SDK Popover for a delayed hover and custom safe
// polygon, making diagonal moves into the submenu more forgiving. The trigger is
// a navigation link, so clicks navigate rather than open the flyout. This is a
// pointer-driven preview, so it does not move or manage keyboard focus.
function FlyoutMenu({
  item,
  visibleChildren,
  collapsed,
  isActive,
}: {
  item: any;
  visibleChildren: any[];
  collapsed: boolean;
  isActive: boolean;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(4), autoPlacement(placementOptions), size(sizeOptions)],
    whileElementsMounted: autoUpdate,
  });
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 100,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      delay: { open: 75 },
      handleClose: safePolygon({ blockPointerEvents: true, requireIntent: false }),
    }),
    useDismiss(context, { ancestorScroll: true }),
  ]);

  return (
    <>
      <MenuItem
        ref={refs.setReference}
        {...getReferenceProps()}
        prefix={item.icon ? <Icon icon={item.icon} /> : undefined}
        component={Link}
        to={visibleChildren[0].to}
        label={!collapsed ? item.label : undefined}
        active={isActive}
        suffix={collapsed ? item.suffix : <Icon size="xs" icon="chevron-right" />}
        data-state={open ? 'open' : 'closed'}
      />
      {isMounted && (
        <FloatingPortal>
          <Box
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...transitionStyles }}
            {...getFloatingProps({ onClick: () => setOpen(false) })}
            className={floatingContainerStyle}
          >
            <Box className={popMenuWrap}>
              <MenuList className={submenu} size="md">
                {visibleChildren.map((child, idx) => (
                  <MenuItem
                    component={NavLink}
                    to={child.to}
                    end={child.end}
                    label={child.label}
                    key={idx}
                    suffix={child.suffix}
                    className={submenuItem}
                  />
                ))}
              </MenuList>
            </Box>
          </Box>
        </FloatingPortal>
      )}
    </>
  );
}

function InlineMenu({ visible, visibleChildren }: { visible: boolean; visibleChildren: any[] }): ReactElement | null {
  const { shouldMount, stage } = useRenderTransition(visible, 150);
  if (!shouldMount) {
    return null;
  }

  return (
    <Box
      className={submenu}
      style={{
        transition: 'opacity .15s linear',
        // maxHeight: stage === 'enter' ? '100vh' : '0px',
        opacity: stage === 'enter' ? 1 : 0,
        overflow: 'hidden',
      }}
    >
      <MenuList variant="dark" size="md" data-stage={stage}>
        {visibleChildren.map((item, idx) => (
          <MenuItem
            className={submenuItem}
            component={NavLink}
            to={item.to}
            end={item.end}
            label={item.label}
            key={idx}
            suffix={item.suffix}
          />
        ))}
      </MenuList>
    </Box>
  );
}

function PopMenu({
  collapsed,
  item,
  visibleChildren,
}: {
  collapsed: boolean;
  item: any;
  visibleChildren: any[];
}): ReactElement {
  const locationPathname = useLocation().pathname;
  const toPathname = useResolvedPath(item.to).pathname;
  const isActive = item.end ? locationPathname === toPathname : locationPathname.startsWith(toPathname);
  // @TODO figure out if we event want this
  // if (!collapsed) {
  // if (false) {
  if (!collapsed && isActive) {
    return (
      <MenuItem
        prefix={item.icon ? <Icon icon={item.icon} /> : undefined}
        component={Link}
        to={visibleChildren[0].to}
        label={!collapsed ? item.label : undefined}
        active={isActive}
        // suffix={item.suffix}
        suffix={<Icon icon="chevron-down" size="xs" />}
      >
        <InlineMenu visible={isActive} visibleChildren={visibleChildren} />
      </MenuItem>
    );
  }
  return <FlyoutMenu item={item} visibleChildren={visibleChildren} collapsed={collapsed} isActive={isActive} />;
}

export function Menu({ menu, collapsed }: MenuProps & { collapsed: boolean }): ReactElement {
  const permCheck = useCheck();

  return (
    <MenuList variant="dark" size="lg">
      {menu.map(({ children, visible, ...item }, index) => {
        if (!permCheck(visible)) {
          return null;
        }

        if (children) {
          const visibleChildren = (children || []).filter(({ visible }) => permCheck(visible));

          if (visibleChildren.length) {
            return <PopMenu key={index} item={item} collapsed={collapsed} visibleChildren={visibleChildren} />;
          } else {
            return null;
          }
        }
        return (
          <MenuItem
            prefix={item.icon ? <Icon icon={item.icon} /> : undefined}
            component={NavLink}
            to={item.to}
            end={item.end}
            label={!collapsed ? item.label : undefined}
            key={index}
          />
        );
      })}
    </MenuList>
  );
}
